import React, { useLayoutEffect, useRef } from 'react';
import { func, node, string } from 'prop-types';

export const SERP_PREVIEW_STATUS = {
    LOADING: 'loading',
    LOADED: 'loaded',
    FAILED: 'failed',
};

// Stops the loader even when the browser never reports the outcome.
const LOADER_TIMEOUT_MS = 20000;

const isRendered = element => element.getClientRects().length > 0;

/**
 * An <object> instead of an <iframe>: S3 answers a missing SERP with a 403 AccessDenied XML, which an <iframe>
 * shows and reports as loaded like any page. An <object> renders its children (the fallback) instead.
 * Mount it with key={url}: Chromium keeps showing the fallback when `data` changes in place.
 */
const SerpPreviewFrame = ({ children, className, onStatusChange, title, url }) => {
    const objectRef = useRef(null);
    const fallbackRef = useRef(null);
    const onStatusChangeRef = useRef(onStatusChange);
    onStatusChangeRef.current = onStatusChange;

    useLayoutEffect(() => {
        const objectElement = objectRef.current;
        const fallbackElement = fallbackRef.current;
        let settled = false;

        const settle = status => {
            if (!settled) {
                settled = true;
                onStatusChangeRef.current(status);
            }
        };
        const handleError = () => settle(SERP_PREVIEW_STATUS.FAILED);
        const handleLoad = () =>
            settle(isRendered(fallbackElement) ? SERP_PREVIEW_STATUS.FAILED : SERP_PREVIEW_STATUS.LOADED);
        // WebKit fires no `error` on an HTTP error, it only renders the fallback.
        const observer =
            typeof ResizeObserver === 'function'
                ? new ResizeObserver(() => {
                      if (isRendered(fallbackElement)) {
                          handleError();
                      }
                  })
                : null;
        // Does not settle, so that a 403 that arrives after it still turns the preview into FAILED.
        const timeoutId = setTimeout(() => {
            if (!settled) {
                onStatusChangeRef.current(SERP_PREVIEW_STATUS.LOADED);
            }
        }, LOADER_TIMEOUT_MS);

        onStatusChangeRef.current(SERP_PREVIEW_STATUS.LOADING);
        // Native listeners: React 18 does not dispatch onError for <object>.
        objectElement.addEventListener('load', handleLoad);
        objectElement.addEventListener('error', handleError);
        if (observer) {
            observer.observe(fallbackElement);
        }

        return () => {
            objectElement.removeEventListener('load', handleLoad);
            objectElement.removeEventListener('error', handleError);
            if (observer) {
                observer.disconnect();
            }
            clearTimeout(timeoutId);
        };
    }, [url]);

    return (
        <object aria-label={title} className={className} data={url} ref={objectRef} type="text/html">
            <div className="serp-preview-fallback" ref={fallbackRef}>
                {children}
            </div>
        </object>
    );
};

SerpPreviewFrame.propTypes = {
    children: node.isRequired,
    className: string,
    onStatusChange: func.isRequired,
    title: string.isRequired,
    url: string.isRequired,
};

export default SerpPreviewFrame;
